import { useState } from "react";
import { Button, MultiSelect, Stack, Text, TextInput } from "@mantine/core";
import { FormattedMessage, useIntl } from "react-intl";
import classes from "./ContactDrawer.module.css";
import { BottomSheet } from "./BottomSheet";
import {
  validateEmail,
  validatePhone,
} from "../../utils/contactDetailsValidation";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

// Free-form topics the advisor uses to prepare the meeting. Stored as-is.
const TOPICS = [
  "Min pensionsnivå",
  "Sparat belopp",
  "Förväntad pension",
  "Höja pensionen",
  "Avgifter",
  "Efterlevandeskydd",
  "Placering",
  "Pensionsuttag",
  "Vad som ingår",
  "Annat",
];

interface ContactDrawerProps {
  opened: boolean;
  onClose: () => void;
  initialEmail?: string;
  initialPhone?: string;
  onSubmit: (
    email: string,
    phone: string,
    topics: string[],
  ) => void | Promise<void>;
}

export function ContactDrawer({
  opened,
  onClose,
  initialEmail = "",
  initialPhone = "",
  onSubmit,
}: ContactDrawerProps) {
  const intl = useIntl();
  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState(initialPhone);
  const [topics, setTopics] = useState<string[]>([]);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const emailError =
    emailTouched && !validateEmail(email)
      ? "Ange en giltig e-postadress"
      : undefined;
  const phoneError =
    phoneTouched && !validatePhone(phone)
      ? "Ange ett giltigt telefonnummer (t.ex. 0701234567)"
      : undefined;

  const canSubmit = validateEmail(email) && validatePhone(phone);

  const handleSubmit = async () => {
    setEmailTouched(true);
    setPhoneTouched(true);
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(email, phone, topics);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <BottomSheet
      opened={opened}
      onClose={onClose}
      title={intl.formatMessage({
        id: "app.poaOverview.advisory.contact.headline",
        defaultMessage: "Fyll i dina uppgifter så hör vi av oss",
      })}
    >
      <Stack gap="md">
        <Text c="dimmed" size="md">
          <FormattedMessage
            id="app.poaOverview.advisory.contact.description"
            defaultMessage="En av våra pensionsrådgivare kontaktar dig för att gå igenom din pension och se hur den kan förbättras."
          />
        </Text>

        <TextInput
          label="E-postadress"
          placeholder="mail@mail.com"
          type="email"
          autoComplete="email"
          aria-required="true"
          size="md"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setEmailTouched(true)}
          error={emailError}
          // Block the value, not the label.
          classNames={{ error: classes.inputError, input: REPLAY_BLOCK_CLASS }}
        />
        <TextInput
          label="Telefonnummer"
          placeholder="0701234567"
          type="tel"
          autoComplete="tel"
          aria-required="true"
          size="md"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onBlur={() => setPhoneTouched(true)}
          error={phoneError}
          classNames={{ error: classes.inputError, input: REPLAY_BLOCK_CLASS }}
        />
        <MultiSelect
          label={
            <>
              <span>
                <FormattedMessage
                  id="app.poaOverview.advisory.label.topics"
                  defaultMessage="Vad vill du få hjälp med?"
                />
              </span>
              <span className={classes.optional}>Valfritt</span>
            </>
          }
          description={intl.formatMessage({
            id: "app.poaOverview.advisory.description.topics",
            defaultMessage:
              "Välj ett eller flera ämnen så att rådgivaren kan förbereda sig inför mötet.",
          })}
          placeholder={
            topics.length === 0
              ? intl.formatMessage({
                  id: "app.poaOverview.advisory.placeholder.topics",
                  defaultMessage: "Välj ämnen",
                })
              : undefined
          }
          data={TOPICS}
          value={topics}
          onChange={setTopics}
          size="md"
          clearable
          classNames={{ label: classes.labelRow, pill: classes.pill }}
        />

        <Button
          fullWidth
          onClick={handleSubmit}
          loading={submitting}
          disabled={!canSubmit || submitting}
        >
          <FormattedMessage
            id="app.poaOverview.advisory.contact.CTA"
            defaultMessage="Kontakta mig"
          />
        </Button>
        <Button
          fullWidth
          variant="default"
          onClick={onClose}
          data-testid="contact-close"
        >
          Stäng
        </Button>
      </Stack>
    </BottomSheet>
  );
}
